import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function TermsPageEn() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) =>
          getSlugVal(s) === 'pmb-salgs-leveringsbetingelser' ||
          getSlugVal(s) === 'terms-and-conditions'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'General Terms and Conditions of Sale & Delivery';
  const badge = pageData?.undertitel || 'Legal Terms';
  const seoTitle =
    pageData?.seo_titel || 'PMB Sales and Delivery Terms & Conditions';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'General terms and conditions of sale and delivery for POVL MØLLERS MASKINFABRIK A/S. Validated and updated.';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Terms & Conditions</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-14 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-wider text-[#0d8086] block mb-2">
            {badge}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            {title}
          </h1>
          <p className="mt-2 text-stone-400 text-sm">Updated January 2025</p>
        </div>
      </section>

      {/* Terms Body */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-stone-800 text-sm leading-relaxed space-y-8">
          {pageData?.indhold ? (
            <div
              className="cms-content space-y-6 [&_h2]:text-base [&_h2]:font-bold [&_h2]:text-stone-900 [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:border-b [&_h2]:border-stone-200 [&_h2]:pb-2 [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3"
              dangerouslySetInnerHTML={{ __html: pageData.indhold }}
            />
          ) : (
            <>
              <div>
                <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
                  1. VALIDITY
                </h2>
            <p className="mb-2">
              1.1 These General Terms and Conditions of Sale and Delivery apply to all supplies of products, assembly, installation, and service work performed by Povl Møllers Maskinfabrik A/S, unless otherwise explicitly agreed in writing.
            </p>
            <p>
              1.2 Povl Møllers Maskinfabrik A/S is not bound by any purchasing conditions stipulated by the buyer, even if Povl Møllers Maskinfabrik has not raised objections to such terms. In addition, Orgalim S 2022 applies to the delivery, unless assembly/erection is included, in which case Orgalim SI 14 applies. In the event of discrepancy between these terms and Orgalim conditions, Orgalim's standard provisions take precedence.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              2. QUOTATIONS & OFFERS
            </h2>
            <p>
              2.1 All quotations are submitted subject to prior sale (cf. Clause 3). If the seller submits a quotation that does not state a specific acceptance deadline, the offer lapses if written acceptance has not reached the seller within 15 days from the date of the quotation.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              3. PRIOR SALE (INTERMEDIARY SALE)
            </h2>
            <p className="mb-2">
              3.1 Until the buyer's acceptance has reached the seller, the seller is entitled to enter into an agreement with a third party regarding the offered items, resulting in the quotation to the buyer lapsing without further notice.
            </p>
            <p className="mb-2">
              3.2 Quotations from Povl Møllers Maskinfabrik are non-binding unless explicitly stated otherwise in the quotation.
            </p>
            <p>
              3.3 Final agreements for delivery are only concluded upon the buyer's receipt of Povl Møllers Maskinfabrik's written order confirmation, and only its contents are legally binding between the parties.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              4. PRICES
            </h2>
            <p className="mb-2">
              4.1 All prices are stated in Danish Kroner (DKK) or Euro (EUR) and are exclusive of VAT and any applicable duties. The buyer is obliged until delivery to accept adjustments in price resulting from documented increased costs for the seller caused by changes in exchange rates, customs duties, taxes, or statutory surcharges relating to the agreed delivery.
            </p>
            <p>
              4.2 If the sold goods are subject to a price list used by the seller, pricing is determined based on the price list in effect on the date of delivery.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              5. PAYMENT TERMS
            </h2>
            <p className="mb-2">
              5.1 Payment must be effected no later than the date stated on the invoice as the due date. If no date is stated, payment must be made in cash upon delivery.
            </p>
            <p className="mb-2">
              5.2 If delivery is postponed due to circumstances on the buyer's side, the buyer is nonetheless obliged to make all payments to the seller as if delivery had occurred at the agreed time, unless the seller confirms otherwise in writing.
            </p>
            <p className="mb-2">
              5.3 If payment is received after the due date, the buyer is liable to pay default interest on the overdue amount at the rate of 2% per commenced month.
            </p>
            <p>
              5.4 The buyer is not entitled to set off counterclaims against the seller unless recognized in writing by the seller, and the buyer has no right to withhold any part of the purchase price on account of disputes or claims of any kind.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              6. RETENTION OF TITLE
            </h2>
            <p className="mb-2">
              6.1 The seller reserves ownership of the sold goods until the entire purchase price, including any accrued interest and costs, has been paid in full to the seller or to the party to whom the claim has been assigned.
            </p>
            <p>
              6.2 In the event of conversion or processing of the sold goods, retention of title is maintained so that it encompasses the converted or processed item to an extent corresponding to the value the sold item represented at the time of sale.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              7. DELIVERY
            </h2>
            <p className="mb-2">
              7.1 Delivery is Ex Works (EXW) from the seller's address, regardless of whether the seller by own staff or through a third-party carrier brings the goods to the buyer in accordance with separate agreement.
            </p>
            <p className="mb-2">
              7.2 The delivery time may be agreed for a specific date or a period following conclusion of the contract. It is a prerequisite that all technical information necessary for executing the order has reached the seller at the time of agreement. If delivery is agreed as "delivered, unloaded, and positioned on foundation", the buyer must assist and provide necessary rigging personnel, cranes, forklifts, and suitable lifting gear.
            </p>
            <p className="mb-2">
              7.3 If delivery is delayed, the buyer is solely entitled, by written notice to the seller, to demand delivery and set a reasonable final deadline. If delivery has not occurred within that deadline, the buyer may terminate the contract. If the buyer terminates, the buyer is not entitled to any compensation or reimbursement for direct or indirect losses.
            </p>
            <p>
              7.4 If delivery delay is due to circumstances specified in Clause 12.2 (Force Majeure), the delivery time is extended by the duration of the obstacle. Either party is entitled to cancel the agreement without liability when the obstacle has persisted for more than 3 months.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              8. SHIPMENT & 9. PACKAGING
            </h2>
            <p className="mb-2">
              8.1 All truck transport arranged by the seller is subject to the unloading site being accessible via passable roads and unloading proceeding as specified in Clause 7.2. The buyer is responsible for immediate unloading; demurrage or waiting time is charged to the buyer.
            </p>
            <p>
              9.1 Special packaging requested or necessitated for machinery and equipment is invoiced to the buyer unless explicitly stated as included in the quotation. Packaging materials are only accepted for return by prior written agreement.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              10. PRODUCT ALTERATIONS & 11. DEFECTS AND COMPLAINTS
            </h2>
            <p className="mb-2">
              10.1 The seller reserves the right without prior notice to carry out modifications to agreed specifications if such alterations can be made without inconvenience to the buyer.
            </p>
            <p className="mb-2">
              11.1 Upon arrival, the buyer must immediately conduct such an examination of the goods as proper business practice requires.
            </p>
            <p className="mb-2">
              11.2 If the buyer wishes to invoke a defect, the buyer must give immediate written notice to the seller specifying the nature of the defect.
            </p>
            <p className="mb-2">
              11.3 At the seller's option, defects in the goods will be rectified by repair or by redelivery of replacement components.
            </p>
            <p>
              11.5 If the buyer has not invoked a defect in writing within 1 month after delivery, the right to make a claim is forfeited. For parts replaced or repaired under Clause 11.3, the seller assumes the same warranty obligations for a period of 12 months, provided that the seller's overall liability for defects does not extend beyond 18 months from the original date of delivery.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              12. LIMITATION OF LIABILITY
            </h2>
            <p className="mb-2">
              12.1 The seller is not liable for operating loss, loss of profit, loss of production, loss of contracts, or any other direct or indirect commercial loss arising in connection with the contract or resulting from delays or defects in the goods.
            </p>
            <p>
              12.2 Force Majeure: Labor disputes, strikes, fires, wars, military mobilization, currency restrictions, insurrections, shortage of transport means, general scarcity of raw materials, power restrictions, and delays or defaults from subcontractors constitute grounds for relief from liability.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              13. WARRANTY & 14. USED MACHINERY
            </h2>
            <p className="mb-2">
              13.1 When purchasing new machinery, products, and replacement parts, the warranty period is 12 months under normal operation of maximum 8 hours per day.
            </p>
            <p className="mb-2">
              13.2 For control systems and electrical equipment, the warranty period is 12 months, provided that no unauthorized intervention or modification has been made by third parties other than Povl Møllers Maskinfabrik.
            </p>
            <p className="mb-2">
              14.1 Fully refurbished used machines are delivered with 12 months warranty on terms matching Clause 13.1.
            </p>
            <p>
              14.2 Other used machinery not described as refurbished is sold as inspected second-hand equipment and carries a 3-month warranty unless explicitly agreed otherwise in the order confirmation.
            </p>
          </div>

          <div>
            <h2 className="text-base font-bold text-stone-900 uppercase tracking-wide border-b border-stone-200 pb-2 mb-3">
              18. DISPUTES AND GOVERNING LAW
            </h2>
            <p>
              18.1 Any dispute between the parties arising out of or in connection with the agreement shall be settled by the ordinary Danish courts in accordance with the substantive laws of Denmark.
            </p>
          </div>

            </>
          )}

          <div className="pt-6 border-t border-stone-200">
            <Link
              to="/en/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded bg-[#006368] text-white font-semibold text-xs uppercase tracking-wider hover:bg-[#004d51] transition"
            >
              <span>Have questions regarding our terms? Contact us</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
